import React, { useState, useEffect } from 'react';
import StudentTable from '../components/WalkinDashboard/StudentTable';
import ActiveFilters from '../components/WalkinDashboard/ActiveFilters';
import Pagination from '../components/WalkinDashboard/Pagination';
import EditStudentPopup from '../components/WalkinDashboard/EditStudentPopup';
import AdvancedSearchPopup from '../components/WalkinDashboard/AdvancedSearchPopup';
import { fetchStudents, fetchFilterOptions } from '../services/studentServices';
import { useDebounce } from '../utils/hooks';
import { DashboardCharts } from '../components/WalkinDashboard/DashboardCharts';
import '../styles/WalkinDashboard.css';

const WalkinDashboard = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showTodayOnly, setShowTodayOnly] = useState(false);
    const [showTodayFollowUpOnly, setShowTodayFollowUpOnly] = useState(false);

    // Add filter states - now includes advanced search filters
    const [selectedFilters, setSelectedFilters] = useState({
        location: '',
        branch: '',
        courseType: '',
        courseName: '',
        enrollmentStatus: '',
        source: '',
    });

    const [filterOptions, setFilterOptions] = useState({
        locations: [],
        branches: [],
        courseTypes: ['CADD', 'Pumo Tech IT', 'Pumo Tech Automation', 'Monz Creative School'],
    });

    const studentsPerPage = 10;

    // New state for popups
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [currentStudent, setCurrentStudent] = useState(null);

    // Use debounce hook for search
    const debouncedSearchTerm = useDebounce(searchTerm, 1000);

    // Fetch available filter options (locations and branches)
    useEffect(() => {
        const getFilterOptions = async () => {
            try {
                const options = await fetchFilterOptions();
                setFilterOptions(prev => ({
                    ...prev,
                    branches: options.branch || [],
                    locations: options.location || [],
                }));
            } catch (err) {
                console.error("Error fetching filter options:", err);
            }
        };

        getFilterOptions();
    }, []);

    // Fetch students based on filters
    useEffect(() => {
        const getStudents = async () => {
            try {
                setLoading(true);
                const studentsData = await fetchStudents({
                    createdToday: showTodayOnly,
                    followUpToday: showTodayFollowUpOnly,
                    search: debouncedSearchTerm,
                    ...selectedFilters
                });

                setStudents(studentsData);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching students:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        getStudents();
    }, [showTodayOnly, debouncedSearchTerm, showTodayFollowUpOnly, selectedFilters]);

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    // Handle filter change
    const handleFilterChange = (type, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [type]: value
        }));
        setCurrentPage(1); // Reset to first page when filters change
    };

    // Clear all filters function
    const clearFilters = () => {
        setShowTodayOnly(false);
        setShowTodayFollowUpOnly(false);
        setSearchTerm('');
        setSelectedFilters({
            location: '',
            branch: '',
            courseType: '',
            courseName: '',
            enrollmentStatus: '',
            source: '',
        });
        setCurrentPage(1);
    };

    // Handle advanced search
    const handleAdvancedSearchApply = (filters) => {
        setSelectedFilters(prev => ({
            ...prev,
            ...filters
        }));
        setCurrentPage(1);
    };

    const handleEdit = (student) => {
        setCurrentStudent(student);
        setShowEditPopup(true);
    };

    const handleClosePopup = () => {
        setShowEditPopup(false);
        setCurrentStudent(null);
    };

    const handleSaveStudent = (updatedStudent) => {
        // Update the students list with the updated student info
        setStudents(prevStudents =>
            prevStudents.map(student =>
                student.id === updatedStudent.id ? updatedStudent : student
            )
        );

        // Close the popup
        handleClosePopup();
    };

    // Handle radio button changes
    const handleRadioChange = (value) => {
        if (value === 'all') {
            setShowTodayOnly(false);
            setShowTodayFollowUpOnly(false);
        } else if (value === 'today') {
            setShowTodayOnly(true);
            setShowTodayFollowUpOnly(false);
        } else if (value === 'followup') {
            setShowTodayOnly(false);
            setShowTodayFollowUpOnly(true);
        }
    };

    // Get current radio value
    const getCurrentRadioValue = () => {
        if (showTodayOnly) return 'today';
        if (showTodayFollowUpOnly) return 'followup';
        return 'all';
    };

    // Pagination logic
    const getCurrentStudents = () => {
        const indexOfLastStudent = currentPage * studentsPerPage;
        const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
        return students.slice(indexOfFirstStudent, indexOfLastStudent);
    };

    const currentStudents = getCurrentStudents();
    const totalPages = Math.ceil(students.length / studentsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) return <div className="loading-container">Loading...</div>;
    if (error) return <div className="error-message">Error: {error}</div>;

    return (
        <div className="dashboard-container">
            <div className="dashboard-header1">
                <h1 className="dashboard-title">Direct Walkin Dashboard</h1>
            </div>
            
            {/* Updated top filter row - Search and Advanced Search button */}
            <div className="top-filter-row">
                <div className="search-container-inline">
                    <div className="search-input-wrapper">
                        <input
                            type="text"
                            placeholder="Search by ID, Bank, or Branch..."
                            className="search1-input"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>
                </div>

                <button
                    className="advancedwd-search-btn"
                    onClick={() => setShowAdvancedSearch(true)}
                >
                    Advanced Search
                </button>

                <button
                    className="cancel3-btn"
                    onClick={() => window.location.href = '/dashboard'}
                >
                    Back
                </button>
            </div>

            {/* Radio buttons below search */}
            <div className="radio-button-container">     
                <div className="radio-option">
                    <input
                        type="radio"
                        id="today-payments"
                        name="dateFilter"
                        value="today"
                        checked={getCurrentRadioValue() === 'today'}
                        onChange={(e) => handleRadioChange(e.target.value)}
                    />
                    <label htmlFor="today-payments">Today's Payments</label>
                </div>
                
                <div className="radio-option">
                    <input
                        type="radio"
                        id="date-range"
                        name="dateFilter"
                        value="followup"
                        checked={getCurrentRadioValue() === 'followup'}
                        onChange={(e) => handleRadioChange(e.target.value)}
                    />
                    <label htmlFor="date-range">Today's follow up</label>
                </div>
            </div>

            <ActiveFilters
                showTodayOnly={showTodayOnly}
                showTodayFollowUpOnly={showTodayFollowUpOnly}
                selectedFilters={selectedFilters}
                onToggleTodayOnly={() => setShowTodayOnly(false)}
                onToggleFollowUpOnly={() => setShowTodayFollowUpOnly(false)}
                onFilterChange={handleFilterChange}
                onClearAll={clearFilters}
            />

            <DashboardCharts students={students} />

            <StudentTable
                students={currentStudents}
                onEdit={handleEdit}
            />

            {students.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={paginate}
                />
            )}

            {/* Advanced Search Popup */}
            <AdvancedSearchPopup
                isOpen={showAdvancedSearch}
                onClose={() => setShowAdvancedSearch(false)}
                onApplyFilters={handleAdvancedSearchApply}
                currentFilters={selectedFilters}
                filterOptions={filterOptions}
            />

            {showEditPopup && currentStudent && (
                <EditStudentPopup
                    student={currentStudent}
                    onClose={handleClosePopup}
                    onSave={handleSaveStudent}
                />
            )}
        </div>
    );
};

export default WalkinDashboard;